import { useEffect, useState } from "react";
import {
	Box,
	Button,
	Card,
	CardContent,
	Container,
	Divider,
	IconButton,
	TextField,
	Typography,
	Alert,
	MenuItem
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import { addDoc, collection, deleteDoc, doc, getDocs, serverTimestamp, updateDoc, query, where } from "firebase/firestore";
import { db } from "../firebase";

import NavBarAdm from "./navbarAdm";
import ShowDienstNummerSubcollections from "./ShowDienstNummerSubcollections";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const emptyPdf = {
	pdfUrl: "",
	dienstLinie: [""],
	description: "",
	shortDateStart: "",
	shortDateEnd: "",
	isShortRange: false,
	order: 1
};

export const AdmPlans = () => {
	const [dateStart, setDateStart] = useState("");
	const [dateEnd, setDateEnd] = useState("");
	const [pdfItems, setPdfItems] = useState([{ ...emptyPdf }]);
	const [saving, setSaving] = useState(false);
	const [deletingExtractKey, setDeletingExtractKey] = useState(null);
	const [deleteExtractNotices, setDeleteExtractNotices] = useState({});
	const [message, setMessage] = useState("");
	const [messageType, setMessageType] = useState("info");
	const [rows, setRows] = useState([]);
	const [editingRowId, setEditingRowId] = useState(null);
	const [editingPdfs, setEditingPdfs] = useState([]);
	const [removedPdfIds, setRemovedPdfIds] = useState([]);
	const [editingDateStart, setEditingDateStart] = useState("");
	const [editingDateEnd, setEditingDateEnd] = useState("");
	// State for extraction results per PDF index in edit mode
	const [extractionResults, setExtractionResults] = useState({});
	// State to show loading info under each PDF extract button
	const [extractingPdfs, setExtractingPdfs] = useState({});
	// State for dienst collections from FahrplaenePages per PDF index
	const [dienstCollections, setDienstCollections] = useState({});

	// Feiertage (Holidays) state
	const [holidayDate, setHolidayDate] = useState(null);
	const [holidayDaycode, setHolidayDaycode] = useState("");
	const [holidays, setHolidays] = useState([]);
	const [holidayMsg, setHolidayMsg] = useState("");
	const [holidayMsgType, setHolidayMsgType] = useState("info");

	// Load holidays from Firestore
	const loadHolidays = async () => {
		try {
			const snapshot = await getDocs(collection(db, "Feiertage"));
			const holidaysData = snapshot.docs.map(doc => ({
				id: doc.id,
				...doc.data()
			}));
			setHolidays(holidaysData);
		} catch (e) {
			setHolidayMsg("Feiertage konnten nicht geladen werden.");
			setHolidayMsgType("error");
		}
	};

	const deleteSubcollectionDocs = async (docId, subcollectionName) => {
		const subColRef = collection(db, "FahrplaenePages", docId, subcollectionName);
		const subSnap = await getDocs(subColRef);
		for (const subDoc of subSnap.docs) {
			await deleteDoc(subDoc.ref);
		}
	};

	const handleDeletePdfExtract = async (startDateValue, endDateValue, rowKey = null) => {
		if (!startDateValue || !endDateValue) {
			setMessage("Bitte Startdatum und Enddatum ausfuellen.");
			setMessageType("warning");
			return;
		}

		const effectiveKey = rowKey || `${startDateValue}__${endDateValue}`;
		setDeletingExtractKey(effectiveKey);
		setDeleteExtractNotices((prev) => ({ ...prev, [effectiveKey]: null }));
		setMessage("");

		try {
			const q = query(
				collection(db, "FahrplaenePages"),
				where("startDate", "==", startDateValue),
				where("endDate", "==", endDateValue)
			);
			const snap = await getDocs(q);

			if (snap.empty) {
				setMessage("Kein PDF-Extract für diesen Datumsbereich gefunden.");
				setMessageType("info");
				setDeleteExtractNotices((prev) => ({
					...prev,
					[effectiveKey]: { text: "Kein Extract gefunden.", severity: "info" }
				}));
				return;
			}

			for (const docSnap of snap.docs) {
				const data = docSnap.data();
				const dienstList = Array.isArray(data.dienstNummerList) ? data.dienstNummerList : [];

				for (const dienstNummer of dienstList) {
					await deleteSubcollectionDocs(docSnap.id, String(dienstNummer));
				}

				// Backward compatibility: remove optional 'pages' subcollection if it exists.
				await deleteSubcollectionDocs(docSnap.id, "pages");

				await deleteDoc(docSnap.ref);
			}

			setMessage(`PDF-Extract gelöscht (${snap.docs.length} Datumsbereich(e)).`);
			setMessageType("success");
			setDeleteExtractNotices((prev) => ({
				...prev,
				[effectiveKey]: { text: "PDF-Extract geloescht.", severity: "success" }
			}));
		} catch (error) {
			console.error("Failed to delete PDF extract:", error);
			setMessage("Loeschen des PDF-Extracts fehlgeschlagen.");
			setMessageType("error");
			setDeleteExtractNotices((prev) => ({
				...prev,
				[effectiveKey]: { text: "Loeschen fehlgeschlagen.", severity: "error" }
			}));
		} finally {
			setDeletingExtractKey(null);
		}
	};

	useEffect(() => {
		loadHolidays();
	}, []);

	// Save a new holiday to Firestore
	const handleAddHoliday = async () => {
		if (!holidayDate) {
			setHolidayMsg("Bitte ein Datum wählen.");
			setHolidayMsgType("warning");
			return;
		}
		if (!holidayDaycode) {
			setHolidayMsg("Bitte einen Wochentag wählen.");
			setHolidayMsgType("warning");
			return;
		}
		const isoDate = dayjs(holidayDate).format("YYYY-MM-DD");
		if (holidays.some(h => h.date === isoDate)) {
			setHolidayMsg("Dieses Datum ist bereits als Feiertag gespeichert.");
			setHolidayMsgType("info");
			return;
		}
		try {
			await addDoc(collection(db, "Feiertage"), { date: isoDate, daycode: holidayDaycode });
			setHolidayMsg("Feiertag gespeichert.");
			setHolidayMsgType("success");
			setHolidayDate(null);
			setHolidayDaycode("");
			loadHolidays();
		} catch (e) {
			setHolidayMsg("Feiertag konnte nicht gespeichert werden.");
			setHolidayMsgType("error");
		}
	};

	const normalizeOrder = (value, fallback) => {
		const parsed = Number.parseInt(value, 10);
		if (Number.isNaN(parsed) || parsed < 1) return fallback;
		return parsed;
	};

	const normalizeDienstLinieValue = (value) => {
		return String(value).replace(/\D/g, "").slice(0, 2);
	};

	const normalizeDienstLinieArray = (value) => {
		if (Array.isArray(value)) {
			return value.map((item) => normalizeDienstLinieValue(item));
		}
		if (typeof value === "string") {
			const parts = value.split(",").map((item) => item.trim());
			return parts.map((item) => normalizeDienstLinieValue(item));
		}
		return [""];
	};

	const getValidDienstLinie = (value) => {
		const values = normalizeDienstLinieArray(value).filter((item) => /^\d{2}$/.test(item));
		return Array.from(new Set(values)).sort((a, b) => Number(a) - Number(b));
	};

	const hasInvalidDienstLinie = (value) => {
		return normalizeDienstLinieArray(value).some(
			(item) => item !== "" && !/^\d{2}$/.test(item)
		);
	};


	const parseDate = (value) => {
		const parsed = new Date(value);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	};

	const formatDate = (value) => {
		const parsed = parseDate(value);
		if (!parsed) return value;
		const day = String(parsed.getDate()).padStart(2, "0");
		const month = String(parsed.getMonth() + 1).padStart(2, "0");
		const year = String(parsed.getFullYear());
		return `${day}-${month}-${year}`;
	};

	const getPdfHref = (value) => {
		if (!value) return "";
		if (/^https?:\/\//i.test(value)) return value;
		const path = value.startsWith("/") ? value : `/${value}`;
		return `https://infogig.link${path}`;
	};

	const hasOverlap = (startValue, endValue, excludeId = null) => {
		const start = parseDate(startValue);
		const end = parseDate(endValue);
		if (!start || !end) return true;
		if (start > end) return true;

		return rows.some((row) => {
			if (excludeId && row.id === excludeId) return false;
			const rowStart = parseDate(row.dateStart);
			const rowEnd = parseDate(row.dateEnd);
			if (!rowStart || !rowEnd) return false;
			return start <= rowEnd && end >= rowStart;
		});
	};

	const loadRows = async () => {
		try {
			const snapshot = await getDocs(collection(db, "Fahrplaene"));
			const entries = [];

			for (const docSnap of snapshot.docs) {
				const data = docSnap.data();
				const pdfSnap = await getDocs(collection(db, "Fahrplaene", docSnap.id, "pdfs"));
				const pdfs = pdfSnap.docs
					.map((pdfDoc, index) => ({
						id: pdfDoc.id,
						...pdfDoc.data(),
						order: pdfDoc.data().order ?? index + 1
					}))
					.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

				entries.push({
					id: docSnap.id,
					dateStart: data.dateStart || "",
					dateEnd: data.dateEnd || "",
					pdfs
				});
			}

			entries.sort((a, b) => {
				const aStart = parseDate(a.dateStart);
				const bStart = parseDate(b.dateStart);
				if (!aStart || !bStart) return 0;
				return aStart - bStart;
			});

			setRows(entries);
		} catch (error) {
			console.error("Failed to load Fahrplaene:", error);
		}
	};

	useEffect(() => {
		loadRows();
	}, []);

	const handlePdfChange = (index, field, value) => {
		setPdfItems((prev) => {
			const next = [...prev];
			next[index] = { ...next[index], [field]: value };
			return next;
		});
	};

	const handleDienstLinieChange = (pdfIndex, lineIndex, value) => {
		setPdfItems((prev) => {
			const next = [...prev];
			const lines = normalizeDienstLinieArray(next[pdfIndex]?.dienstLinie);
			lines[lineIndex] = normalizeDienstLinieValue(value);
			next[pdfIndex] = { ...next[pdfIndex], dienstLinie: lines };
			return next;
		});
	};

	const addDienstLinieField = (pdfIndex) => {
		setPdfItems((prev) => {
			const next = [...prev];
			const lines = normalizeDienstLinieArray(next[pdfIndex]?.dienstLinie);
			lines.splice(1, 0, "");
			next[pdfIndex] = { ...next[pdfIndex], dienstLinie: lines };
			return next;
		});
	};

	const addPdfRow = () => {
		setPdfItems((prev) => [...prev, { ...emptyPdf, order: prev.length + 1 }]);
	};

	const handleSave = async () => {
		const hasAnyPdfData = pdfItems.some(
			(item) => item.pdfUrl || item.description || getValidDienstLinie(item.dienstLinie).length > 0
		);
		const hasValidPdf = pdfItems.some(
			(item) => item.pdfUrl && getValidDienstLinie(item.dienstLinie).length > 0
		);
		if (!dateStart || !dateEnd) {
			setMessage("Bitte Datumsbereich ausfuellen.");
			setMessageType("warning");
			return;
		}
		if (!hasAnyPdfData) {
			setMessage("Bitte mindestens ein PDF ausfuellen.");
			setMessageType("warning");
			return;
		}
		if (!hasValidPdf) {
			setMessage("Bitte PDF URL und DienstLinie ausfuellen.");
			setMessageType("warning");
			return;
		}
		const hasInvalidDienst = pdfItems.some((item) => hasInvalidDienstLinie(item.dienstLinie));
		if (hasInvalidDienst) {
			setMessage("DienstLinie muss genau 2 Ziffern haben.");
			setMessageType("warning");
			return;
		}

		if (hasOverlap(dateStart, dateEnd)) {
			setMessage("Der Datumsbereich ueberschneidet sich oder ist ungueltig.");
			setMessageType("warning");
			return;
		}

		setSaving(true);
		setMessage("");

		try {
			const planRef = await addDoc(collection(db, "Fahrplaene"), {
				dateStart,
				dateEnd,
				createdAt: serverTimestamp()
			});

			const validPdfs = pdfItems
				.filter((item) => item.pdfUrl && getValidDienstLinie(item.dienstLinie).length > 0)
				.map((item, index) => ({
					...item,
					order: normalizeOrder(item.order, index + 1),
					dienstLinie: getValidDienstLinie(item.dienstLinie)
				}));
			for (const item of validPdfs) {
				await addDoc(collection(db, "Fahrplaene", planRef.id, "pdfs"), {
					pdfUrl: item.pdfUrl,
					dienstLinie: item.dienstLinie,
					description: item.description || "",
					order: item.order
				});
			}

			setMessage("Erfolgreich gespeichert.");
			setMessageType("success");
			setDateStart("");
			setDateEnd("");
			setPdfItems([{ ...emptyPdf }]);
			await loadRows();
		} catch (error) {
			console.error("Failed to save Fahrplaene:", error);
			setMessage("Speichern fehlgeschlagen. Bitte erneut versuchen.");
			setMessageType("error");
		} finally {
			setSaving(false);
		}
	};

	const startEditRow = async (row) => {
		setDateStart("");
		setDateEnd("");
		setPdfItems([{ ...emptyPdf }]);
		setMessage("");
		setMessageType("info");
		setEditingRowId(row.id);
		const newEditingPdfs = row.pdfs.map((pdf, index) => ({
			...pdf,
			dienstLinie: normalizeDienstLinieArray(pdf.dienstLinie || pdf.title || ""),
			order: pdf.order ?? index + 1
		}));
		setEditingPdfs(newEditingPdfs);
		setRemovedPdfIds([]);
		setEditingDateStart(row.dateStart || "");
		setEditingDateEnd(row.dateEnd || "");
		setExtractionResults({}); // Clear extraction results when starting edit

		// Fetch dienst collections from FahrplaenePages for each PDF
		const newDienstCollections = {};
		for (let pdfIndex = 0; pdfIndex < newEditingPdfs.length; pdfIndex++) {
			const pdf = newEditingPdfs[pdfIndex];
			// Build the date range key
			const dateKey = `${row.dateStart}__${row.dateEnd}`;
			try {
				// Check if the FahrplaenePages doc with the dateKey exists
				const docRef = doc(db, "FahrplaenePages", dateKey);
				const docSnap = await getDocs(collection(db, "FahrplaenePages"));
				let found = false;
				docSnap.forEach(d => { if (d.id === dateKey) found = true; });
				if (!found) {
					newDienstCollections[pdfIndex] = [];
					continue;
				}
				// Get all dienstNummer subdocs
				const dienstColRef = collection(db, "FahrplaenePages", dateKey, "dienstNummer");
				const dienstDocs = await getDocs(dienstColRef);
				// DienstLinie: two digits
				const dienstLinien = normalizeDienstLinieArray(pdf.dienstLinie).filter(l => l.length === 2);
				const dienstLinienSet = new Set(dienstLinien);
				const matching = [];
				dienstDocs.forEach(dienstDoc => {
					const data = dienstDoc.data();
					if (data.dienstNummer && typeof data.dienstNummer === 'string' && data.dienstNummer.length >= 2) {
						const prefix = data.dienstNummer.substring(0, 2);
						if (dienstLinienSet.has(prefix)) {
							matching.push(data);
						}
					}
				});
				newDienstCollections[pdfIndex] = matching;
			} catch (err) {
				newDienstCollections[pdfIndex] = [];
			}
		}
		setDienstCollections(newDienstCollections);
	};

	const cancelEditRow = () => {
		setEditingRowId(null);
		setEditingPdfs([]);
		setRemovedPdfIds([]);
		setEditingDateStart("");
		setEditingDateEnd("");
	};

	const handleEditPdfChange = (index, field, value) => {
		setEditingPdfs((prev) => {
			const next = [...prev];
			next[index] = { ...next[index], [field]: value };
			return next;
		});
	};

	const handleEditDienstLinieChange = (pdfIndex, lineIndex, value) => {
		setEditingPdfs((prev) => {
			const next = [...prev];
			const lines = normalizeDienstLinieArray(next[pdfIndex]?.dienstLinie);
			lines[lineIndex] = normalizeDienstLinieValue(value);
			next[pdfIndex] = { ...next[pdfIndex], dienstLinie: lines };
			return next;
		});
	};

	const addEditDienstLinieField = (pdfIndex) => {
		setEditingPdfs((prev) => {
			const next = [...prev];
			const lines = normalizeDienstLinieArray(next[pdfIndex]?.dienstLinie);
			lines.splice(1, 0, "");
			next[pdfIndex] = { ...next[pdfIndex], dienstLinie: lines };
			return next;
		});
	};

	const addEditPdfRow = () => {
		setEditingPdfs((prev) => [...prev, { ...emptyPdf, order: prev.length + 1 }]);
	};

	const addShortRangePdfRow = () => {
		setEditingPdfs((prev) => [
			...prev,
			{ ...emptyPdf, isShortRange: true, order: prev.length + 1 }
		]);
	};

	const removeEditPdfRow = (index) => {
		setEditingPdfs((prev) => {
			const target = prev[index];
			if (target?.id) {
				setRemovedPdfIds((ids) => [...ids, target.id]);
			}
			return prev.filter((_, i) => i !== index);
		});
	};

	const saveEditRow = async (rowId) => {
		setSaving(true);
		setMessage("");

		try {
			const hasEmptyNewPdf = editingPdfs.some((pdf) => {
				const hasDienstLinie = getValidDienstLinie(pdf.dienstLinie).length > 0;
				return !pdf.id && !pdf.pdfUrl && !hasDienstLinie && !pdf.description;
			});
			if (hasEmptyNewPdf) {
				setMessage("Bitte leere PDF-Felder ausfuellen oder entfernen.");
				setMessageType("warning");
				setSaving(false);
				return;
			}
			const hasInvalidDienst = editingPdfs.some((pdf) => hasInvalidDienstLinie(pdf.dienstLinie));
			if (hasInvalidDienst) {
				setMessage("DienstLinie muss genau 2 Ziffern haben.");
				setMessageType("warning");
				setSaving(false);
				return;
			}

			const validPdfs = editingPdfs
				.filter((pdf) => pdf.pdfUrl && getValidDienstLinie(pdf.dienstLinie).length > 0)
				.map((pdf, index) => ({
					...pdf,
					order: normalizeOrder(pdf.order, index + 1),
					dienstLinie: getValidDienstLinie(pdf.dienstLinie)
				}));

			if (validPdfs.length === 0) {
				const pdfIdsToDelete = new Set([
					...removedPdfIds,
					...editingPdfs.filter((pdf) => pdf.id).map((pdf) => pdf.id)
				]);

				for (const pdfId of pdfIdsToDelete) {
					await deleteDoc(doc(db, "Fahrplaene", rowId, "pdfs", pdfId));
				}

				await deleteDoc(doc(db, "Fahrplaene", rowId));
				setMessage("Eintrag entfernt, da keine PDFs mehr vorhanden sind.");
				setMessageType("success");
				cancelEditRow();
				await loadRows();
				return;
			}

			if (hasOverlap(editingDateStart, editingDateEnd, rowId)) {
				setMessage("Der Datumsbereich ueberschneidet sich oder ist ungueltig.");
				setMessageType("warning");
				setSaving(false);
				return;
			}

			await updateDoc(doc(db, "Fahrplaene", rowId), {
				dateStart: editingDateStart,
				dateEnd: editingDateEnd
			});

			for (const pdfId of removedPdfIds) {
				await deleteDoc(doc(db, "Fahrplaene", rowId, "pdfs", pdfId));
			}

			for (const pdf of validPdfs) {
				if (pdf.id) {
					await updateDoc(doc(db, "Fahrplaene", rowId, "pdfs", pdf.id), {
						pdfUrl: pdf.pdfUrl,
						dienstLinie: pdf.dienstLinie,
						description: pdf.description || "",
						shortDateStart: pdf.shortDateStart || "",
						shortDateEnd: pdf.shortDateEnd || "",
						isShortRange: !!pdf.isShortRange,
						order: pdf.order
					});
				} else {
					await addDoc(collection(db, "Fahrplaene", rowId, "pdfs"), {
						pdfUrl: pdf.pdfUrl,
						dienstLinie: pdf.dienstLinie,
						description: pdf.description || "",
						shortDateStart: pdf.shortDateStart || "",
						shortDateEnd: pdf.shortDateEnd || "",
						isShortRange: !!pdf.isShortRange,
						order: pdf.order
					});
				}
			}

			setMessage("Aenderungen erfolgreich gespeichert.");
			setMessageType("success");
			cancelEditRow();
			await loadRows();
		} catch (error) {
			console.error("Failed to update Fahrplaene:", error);
			setMessage("Aktualisierung fehlgeschlagen. Bitte erneut versuchen.");
			setMessageType("error");
		} finally {
			setSaving(false);
		}
	};

	return (
		<>
			<NavBarAdm />
			<Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>

				{/* Feiertage Verwaltung */}
				<Card sx={{ mb: 4 }}>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							Feiertage (Holidays)
						</Typography>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
								<DatePicker
									label="Feiertag wählen"
									value={holidayDate}
									onChange={setHolidayDate}
									format="DD.MM.YYYY"
								/>
								<TextField
									select
									label="Wochentag wählen"
									value={holidayDaycode}
									onChange={e => setHolidayDaycode(e.target.value)}
									sx={{ minWidth: 120 }}
								>
									<MenuItem value="Mo">Mo</MenuItem>
									<MenuItem value="Di">Di</MenuItem>
									<MenuItem value="Mi">Mi</MenuItem>
									<MenuItem value="Do">Do</MenuItem>
									<MenuItem value="Fr">Fr</MenuItem>
									<MenuItem value="Sa">Sa</MenuItem>
									<MenuItem value="SF">SF</MenuItem>
								</TextField>
								<Button variant="contained" onClick={handleAddHoliday}>
									Hinzufügen
								</Button>
							</Box>
						</LocalizationProvider>
						{holidayMsg && (
							<Alert severity={holidayMsgType} sx={{ mb: 2 }}>{holidayMsg}</Alert>
						)}
						<Box>
							<Typography variant="body2" sx={{ mb: 1 }}>Gespeicherte Feiertage:</Typography>
							<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
								{holidays.length === 0 ? (
									<Typography variant="body2">Keine Feiertage gespeichert.</Typography>
								) : (
									holidays.map((h) => (
										<Box key={h.id || h.date} sx={{ px: 1, py: 0.5, bgcolor: "#eee", borderRadius: 1, fontSize: 14 }}>
											{dayjs(h.date).format("DD.MM.YYYY")} ({h.daycode})
										</Box>
									))
								)}
							</Box>
						</Box>
					</CardContent>
				</Card>
				<Typography variant="h4" gutterBottom>
					Fahrplaene Verwaltung
				</Typography>

				<Card sx={{ mb: 4 }}>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							Neuer Eintrag
						</Typography>

						{message && (
							<Alert severity={messageType} sx={{ mb: 2 }}>
								{message}
							</Alert>
						)}

						<Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
							<TextField
								label="Startdatum"
								type="date"
								value={dateStart}
								onChange={(e) => setDateStart(e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>
							<TextField
								label="Enddatum"
								type="date"
								value={dateEnd}
								onChange={(e) => setDateEnd(e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>
						</Box>

						<Divider sx={{ mb: 2 }} />

						{pdfItems.map((item, index) => (
							<Box
								key={index}
								sx={{
									display: "flex",
									gap: 2,
									flexWrap: "wrap",
									alignItems: "center",
									mb: 2
								}}
							>
								<TextField
									label="Nr."
									type="number"
									value={item.order}
									onChange={(e) => handlePdfChange(index, "order", e.target.value)}
									sx={{ width: 100 }}
									inputProps={{ min: 1 }}
								/>
								<TextField
									label="PDF URL"
									value={item.pdfUrl}
									onChange={(e) => handlePdfChange(index, "pdfUrl", e.target.value)}
									fullWidth
								/>
									<IconButton color="primary" onClick={() => addDienstLinieField(index)}>
										<AddIcon />
									</IconButton>
									{normalizeDienstLinieArray(item.dienstLinie)
										.slice(1)
										.map((value, lineIndex) => (
											<TextField
												key={lineIndex + 1}
												label="DienstLinie"
												value={value}
												onChange={(e) =>
													handleDienstLinieChange(index, lineIndex + 1, e.target.value)
												}
												sx={{ width: 80 }}
												inputProps={{ inputMode: "numeric", maxLength: 2 }}
											/>
										))}
									<TextField
										label="DienstLinie"
										value={normalizeDienstLinieArray(item.dienstLinie)[0] || ""}
										onChange={(e) => handleDienstLinieChange(index, 0, e.target.value)}
										sx={{ width: 80 }}
										inputProps={{ inputMode: "numeric", maxLength: 2 }}
									/>
								<TextField
									label="Beschreibung"
									value={item.description}
									onChange={(e) => handlePdfChange(index, "description", e.target.value)}
									fullWidth
								/>
							</Box>
						))}

						

						<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
							<IconButton color="primary" onClick={addPdfRow}>
								<AddIcon />
							</IconButton>
							<Typography variant="body2">Weitere PDF hinzufuegen</Typography>
						</Box>

						<Box sx={{ mt: 3 }}>
							{message && (
							<Alert severity={messageType} sx={{ mb: 2 }}>
								{message}
							</Alert>
						)}
							<Button
								variant="contained"
								startIcon={<SaveIcon />}
								onClick={handleSave}
								disabled={saving}
							>
								Speichern
							</Button>
						</Box>
					</CardContent>
				</Card>

				<Card>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							Bestehende Fahrplaene
						</Typography>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
							{rows.map((row) => {
								const isEditing = editingRowId === row.id;
								const rowDeleteNotice = deleteExtractNotices[row.id];
								const pdfsToRender = isEditing
									? editingPdfs
									: [...row.pdfs].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

								return (
									<Card key={row.id} sx={{ borderRadius: 2, boxShadow: 3 }}>
										<CardContent>
											<Box
												sx={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
													gap: 2,
													flexWrap: "wrap"
												}}
											>
												<Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
													{isEditing ? (
														<>
															<TextField
																label="Startdatum"
																type="date"
																value={editingDateStart}
																onChange={(e) => setEditingDateStart(e.target.value)}
																InputLabelProps={{ shrink: true }}
															/>
															<TextField
																label="Enddatum"
																type="date"
																value={editingDateEnd}
																onChange={(e) => setEditingDateEnd(e.target.value)}
																InputLabelProps={{ shrink: true }}
															/>
															<Button
																variant="outlined"
																color="error"
																onClick={() => handleDeletePdfExtract(editingDateStart, editingDateEnd, row.id)}
																disabled={deletingExtractKey === row.id || !editingDateStart || !editingDateEnd}
															>
																{deletingExtractKey === row.id ? "LOESCHE PDF EXTRACT..." : "DELETE PDF EXTRACT"}
															</Button>
															{rowDeleteNotice?.text && (
																<Typography
																	variant="caption"
																	color={
																		rowDeleteNotice.severity === "success"
																			? "success.main"
																			: rowDeleteNotice.severity === "error"
																				? "error.main"
																				: "text.secondary"
																	}
																	sx={{ ml: 1 }}
																>
																	{rowDeleteNotice.text}
																</Typography>
															)}
														</>
													) : (
														<>
															<Typography variant="h6">
																{formatDate(row.dateStart)} - {formatDate(row.dateEnd)}
															</Typography>
															<Button
																variant="outlined"
																color="error"
																onClick={() => handleDeletePdfExtract(row.dateStart, row.dateEnd, row.id)}
																disabled={deletingExtractKey === row.id}
															>
																{deletingExtractKey === row.id ? "LOESCHE PDF EXTRACT..." : "DELETE PDF EXTRACT"}
															</Button>
															{rowDeleteNotice?.text && (
																<Typography
																	variant="caption"
																	color={
																		rowDeleteNotice.severity === "success"
																			? "success.main"
																			: rowDeleteNotice.severity === "error"
																				? "error.main"
																				: "text.secondary"
																	}
																	sx={{ ml: 1 }}
																>
																	{rowDeleteNotice.text}
																</Typography>
															)}
														</>
													)}
												</Box>

												<Box
													sx={{
													display: "flex",
													gap: 1,
													justifyContent: "flex-end",
													flexDirection: { xs: "column", sm: "row" },
													alignItems: { xs: "stretch", sm: "center" }
												}}
												>
													{isEditing ? (
														<>
															<Button
																variant="contained"
																onClick={() => saveEditRow(row.id)}
																disabled={saving}
																sx={{ width: { xs: "100%", sm: "auto" } }}
															>
																Speichern
															</Button>
															<Button
																onClick={cancelEditRow}
																disabled={saving}
																sx={{ width: { xs: "100%", sm: "auto" } }}
															>
																Abbrechen
															</Button>
														</>
													) : (
														<Button variant="outlined" onClick={() => startEditRow(row)}>
															Bearbeiten
														</Button>
													)}
												</Box>
											</Box>

											<Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 2 }}>
												{pdfsToRender.map((pdf, index) => (
													<Card
														key={pdf.id || index}
														component={isEditing ? "div" : "a"}
														href={isEditing ? undefined : getPdfHref(pdf.pdfUrl)}
														target={isEditing ? undefined : "_blank"}
														rel={isEditing ? undefined : "noreferrer"}
														sx={{
															minWidth: 100,
															maxWidth: 300,
															boxShadow: 2,
															textDecoration: "none",
															color: "inherit"
														}}
													>
														<CardContent>
															{isEditing ? (
																<>
																	<Box
																		sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}
																	>
																		<TextField
																			label="Nr."
																			type="number"
																			value={pdf.order ?? index + 1}
																			onChange={(e) =>
																				handleEditPdfChange(index, "order", e.target.value)
																			}
																			sx={{ width: 100 }}
																			inputProps={{ min: 1 }}
																		/>
																		<TextField
																			label="PDF URL"
																			value={pdf.pdfUrl || ""}
																			onChange={(e) =>
																				handleEditPdfChange(index, "pdfUrl", e.target.value)
																			}
																			fullWidth
																		/>
																		<IconButton color="primary" onClick={() => addEditDienstLinieField(index)}>
																			<AddIcon />
																		</IconButton>
																		{normalizeDienstLinieArray(pdf.dienstLinie)
																			.slice(1)
																			.map((value, lineIndex) => (
																				<TextField
																					key={lineIndex + 1}
																					label="DienstLinie"
																					value={value}
																					onChange={(e) =>
																						handleEditDienstLinieChange(index, lineIndex + 1, e.target.value)
																					}
																					sx={{ width: 80 }}
																					inputProps={{ inputMode: "numeric", maxLength: 2 }}
																				/>
																			))}
																		<TextField
																			label="DienstLinie"
																			value={normalizeDienstLinieArray(pdf.dienstLinie)[0] || ""}
																			onChange={(e) =>
																			handleEditDienstLinieChange(index, 0, e.target.value)
																		}
																			sx={{ width: 80 }}
																		inputProps={{ inputMode: "numeric", maxLength: 2 }}
																		/>
																		<TextField
																			label="Beschreibung"
																			value={pdf.description || ""}
																			onChange={(e) =>
																				handleEditPdfChange(index, "description", e.target.value)
																			}
																			fullWidth
																		/>
																		{/* PDF PAGES EXTRACT Button */}
																		<Button
																			variant="outlined"
																			size="small"
																			sx={{ mt: 1, mb: 1 }}
																			disabled={Boolean(extractingPdfs[index])}
																			onClick={async () => {
																				try {
																					const { extractAndSavePagesFromPdf } = await import("../pages/plans_screen/ExtractForPDFPages");
																					if (!pdf.pdfUrl) {
																						setMessage("PDF URL fehlt!");
																						setMessageType("warning");
																						return;
																					}
																					setExtractingPdfs((prev) => ({ ...prev, [index]: true }));
																					const result = await extractAndSavePagesFromPdf(
																						pdf.pdfUrl,
																						editingDateStart,
																						editingDateEnd
																					);
																					setExtractionResults((prev) => ({ ...prev, [index]: result }));
																				} catch (err) {
																					setMessage("Fehler bei der PDF-Extraktion: " + (err?.message || err));
																					setMessageType("error");
																				} finally {
																					setExtractingPdfs((prev) => ({ ...prev, [index]: false }));
																				}
																			}}
																		>
																			PDF PAGES EXTRACT
																		</Button>
																		{extractingPdfs[index] && (
																			<Typography variant="body2" color="text.secondary" sx={{ mt: -0.5, mb: 1 }}>
																				Extrahiere Seiten aus PDF...
																			</Typography>
																		)}
																		{isEditing && (
																			<Card sx={{ mt: 1, mb: 1, p: 1, background: '#f5f5f5', maxHeight: 200, overflowY: 'auto' }}>
																				<Typography variant="subtitle2" sx={{ mb: 1 }}>Extrahierte Bereiche:</Typography>
																				{extractionResults && extractionResults[index] && extractionResults[index].length > 0 ? (
																					extractionResults[index].map((entry, i) => (
																						<Box key={i} sx={{ mb: 1, p: 1, border: '1px solid #ccc', borderRadius: 1 }}>
																							<Typography variant="body2">dienstNummer: <b>{entry.dienstNummer}</b></Typography>
																							<Typography variant="body2">daycode: <b>{entry.daycode}</b></Typography>
																							<Typography variant="body2">Seiten: <b>{entry.startPage} - {entry.endPage}</b></Typography>
																						</Box>
																					))
																				) : dienstCollections && dienstCollections[index] && dienstCollections[index].length > 0 ? (
																					dienstCollections[index].map((entry, i) => (
																						<Box key={i} sx={{ mb: 1, p: 1, border: '1px solid #ccc', borderRadius: 1 }}>
																							<Typography variant="body2">dienstNummer: <b>{entry.dienstNummer}</b></Typography>
																							<Typography variant="body2">daycode: <b>{entry.daycode}</b></Typography>
																							<Typography variant="body2">Seiten: <b>{entry.startPage} - {entry.endPage}</b></Typography>
																						</Box>
																					))
																				) : (
																					<ShowDienstNummerSubcollections dateStart={editingDateStart} dateEnd={editingDateEnd} dienstLinie={pdf.dienstLinie} />
																				)}
																			</Card>
																		)}
																		{(pdf.isShortRange || pdf.shortDateStart || pdf.shortDateEnd) && (
																			<>
																				<TextField
																					label="Startdatum"
																					type="date"
																					value={pdf.shortDateStart || ""}
																					onChange={(e) =>
																						handleEditPdfChange(index, "shortDateStart", e.target.value)
																					}
																					InputLabelProps={{ shrink: true }}
																				/>
																				<TextField
																					label="Enddatum"
																					type="date"
																					value={pdf.shortDateEnd || ""}
																					onChange={(e) =>
																						handleEditPdfChange(index, "shortDateEnd", e.target.value)
																					}
																					InputLabelProps={{ shrink: true }}
																				/>
																			</>
																		)}
																	</Box>
																	<Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
																		<Button color="error" onClick={() => removeEditPdfRow(index)}>
																			Entfernen
																		</Button>
																	</Box>
																</>
															) : (
																<>
																	<Typography variant="body2" color="text.secondary">
																		Nr. {pdf.order ?? index + 1}
																	</Typography>
																	{(pdf.isShortRange || pdf.shortDateStart || pdf.shortDateEnd) && (
																		<Typography variant="body2" color="text.secondary">
																			{formatDate(pdf.shortDateStart)} - {formatDate(pdf.shortDateEnd)}
																		</Typography>
																	)}
																	<Typography variant="body1" sx={{ fontWeight: 700, fontSize: 20 }}>
																		{getValidDienstLinie(pdf.dienstLinie).join(", ") || "(ohne DienstLinie)"}
																	</Typography>
																	<Typography variant="body2" color="text.secondary">
																		{pdf.description || ""}
																	</Typography>

																</>
															)}
														</CardContent>
													</Card>
												))}
											</Box>

											{isEditing && (
												<Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
													<Button onClick={addEditPdfRow}>
														Weitere PDF hinzufuegen
													</Button>
													{message && messageType !== "success" && (
														<Typography color="error" sx={{ flexBasis: "100%" }}>
															{message}
														</Typography>
													)}
													<Button
														variant="contained"
														onClick={() => saveEditRow(row.id)}
														disabled={saving}
													>
														Speichern
													</Button>
													<Button onClick={cancelEditRow} disabled={saving}>
														Abbrechen
													</Button>
													<Button
														startIcon={<AddIcon />}
														onClick={addShortRangePdfRow}
													>
														Dienst im kurzen Zeitraum
													</Button>
												</Box>
											)}
										</CardContent>
									</Card>
								);
							})}
						</Box>
					</CardContent>
				</Card>
			</Container>
		</>
	);
};
